// Vahid bildiriş sistemi — "Dynamic Island" üslubu.
//
// NİYƏ: əvvəl bildirişlər react-toastify-ın standart `theme="colored"` kartları
// ilə sağ yuxarıdan çıxırdı. POS ekranı 15–24" toxunmatik olur; yandan çıxan
// dar kart ya sifariş siyahısını örtürdü, ya da gözdən qaçırdı. Mətbəx
// ekranındakı island isə ekranın mərkəzində, iri və oxunaqlı idi. Bu modul
// həmin dizaynı bütün tətbiqə yayır.
//
// NİYƏ SHIM: react-toastify "motor" olaraq qalır (növbə, taymer, hover-də
// dayanma, toastId ilə dublikat qarşısı — hamısı sınaqdan çıxmış kod).
// Biz yalnız GÖRÜNÜŞÜ tam əvəz edirik. Beləliklə ~110 mövcud çağırış
// yerində məntiq dəyişmir, sadəcə import yolu dəyişir.
//
// İSTİFADƏ:
//   notify.success('Yadda saxlanıldı')
//   notify.error(msg, { title: 'Çap xətası', description: e.message })
//   notify.alert('Masa 4 ofisant çağırır', { autoClose: 8000, toastId: 'wc-12' })

import React from 'react';
import {
  toast as rtToast,
  cssTransition,
  type Id,
  type ToastOptions,
} from 'react-toastify';
import { AlertTriangle, Bell, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { tStatic } from '../../i18n/I18nContext';

export type NotifyKind = 'success' | 'error' | 'warning' | 'info' | 'alert';

export interface NotifyAction {
  label: string;
  /** `close` island-ı bağlayır — təsdiq düymələrində lazımdır. */
  onClick: (close: () => void) => void;
  variant?: 'primary' | 'danger' | 'ghost';
}

export interface NotifyOptions extends Omit<ToastOptions, 'type' | 'position' | 'icon'> {
  /** Üstdəki kiçik böyük-hərfli etiket. Verilməsə növə görə seçilir. */
  title?: string;
  /** İkinci sətir — uzun izahat, xəta detalı və s. Verilməsi "kart" rejimini açır. */
  description?: string;
  /** Təsdiq/əməliyyat düymələri. Verilməsi "kart" rejimini açır. */
  actions?: NotifyAction[];
  /**
   * QƏBUL EDİLİR, LAKİN NƏZƏRƏ ALINMIR.
   *
   * Köhnə çağırış yerlərində `position: 'top-right'` qalıb. İstifadəçi məhz
   * yandan çıxan formadan imtina etməyi istəyib, ona görə mövqe qəsdən
   * mərkəzdə sabitlənib. Tipi saxlayırıq ki, ~25 çağırış yerini dəyişmək
   * lazım gəlməsin və gələcəkdə kimsə bunu ötürsə TS xətası verməsin.
   */
  position?: unknown;
  /** QƏBUL EDİLİR, LAKİN NƏZƏRƏ ALINMIR — ikonu island-ın növü müəyyən edir. */
  icon?: unknown;
}

// ─── Növ → görünüş ───────────────────────────────────────────────────────────
// Rənglər Kitchen island-dan gəlir: aşpaz artıq bu koda öyrəşib.
const KIND: Record<
  NotifyKind,
  { icon: React.ElementType; ring: string; labelKey: string }
> = {
  success: { icon: CheckCircle2, ring: 'bg-emerald-500 shadow-emerald-500/40', labelKey: 'notify.success' },
  error: { icon: XCircle, ring: 'bg-red-500 shadow-red-500/40', labelKey: 'notify.error' },
  warning: { icon: AlertTriangle, ring: 'bg-amber-500 shadow-amber-500/40', labelKey: 'notify.warning' },
  info: { icon: Info, ring: 'bg-sky-500 shadow-sky-500/40', labelKey: 'notify.info' },
  alert: { icon: Bell, ring: 'bg-orange-500 shadow-orange-500/40', labelKey: 'notify.alert' },
};

/**
 * Mesaj "iri" sayılırsa kart rejiminə keçirik: mətn sətirlərə bölünür,
 * pill genişlənir və bağlama düyməsi görünür.
 *
 * NİYƏ 48: tipik POS mesajı ("Yadda saxlanıldı", "Mətbəxə göndərildi") 20–35
 * simvoldur və bir sətirdə pill kimi daha yaxşı görünür. Server xətaları,
 * validasiya siyahıları isə 60+ simvol olur və sətrə sığmır.
 */
const LONG_TEXT = 48;

function isLarge(message: string, opts: NotifyOptions): boolean {
  if (opts.description || (opts.actions && opts.actions.length > 0)) return true;
  return message.length > LONG_TEXT || message.includes('\n');
}

const ACTION_STYLE: Record<NonNullable<NotifyAction['variant']>, string> = {
  primary: 'bg-white text-gray-900 hover:bg-gray-200',
  danger: 'bg-red-600 text-white hover:bg-red-500',
  ghost: 'bg-white/10 text-gray-200 hover:bg-white/20',
};

interface IslandProps {
  kind: NotifyKind;
  title: string;
  message: string;
  description?: string;
  actions?: NotifyAction[];
  large: boolean;
  /** react-toastify məzmun elementini klonlayarkən bu prop-u özü ötürür. */
  closeToast?: () => void;
}

const Island: React.FC<IslandProps> = ({
  kind,
  title,
  message,
  description,
  actions,
  large,
  closeToast,
}) => {
  const spec = KIND[kind];
  const Icon = spec.icon;
  const close = closeToast ?? (() => undefined);

  return (
    <div
      // Səth — fon, saydamlıq, blur, kölgə və açıq/tünd fərqi — index.css-dəki
      // `.rz-island-surface` sinifindədir. Tailwind sinifləri ilə yazanda
      // backdrop-filter zənciri (blur + saturate) oxunmaz olurdu.
      className={[
        'rz-island-surface pointer-events-auto flex items-start gap-3',
        large ? 'rounded-3xl px-3 py-3 pr-4 sm:gap-4' : 'rounded-full px-3 py-2.5 pr-5 items-center',
      ].join(' ')}
    >
      <div
        className={`shrink-0 rounded-full flex items-center justify-center shadow-lg ${spec.ring} ${
          large ? 'w-11 h-11' : 'w-10 h-10'
        }`}
      >
        <Icon className={large ? 'w-6 h-6 text-white' : 'w-5 h-5 text-white'} />
      </div>

      <div className="flex flex-col leading-tight min-w-0 py-0.5">
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
          {title}
        </span>
        <span
          className={[
            'font-bold text-white',
            large ? 'text-[15px] break-words' : 'text-sm whitespace-nowrap',
          ].join(' ')}
        >
          {message}
        </span>
        {description && (
          // rz-clamp-3: server stack-trace qaytara bilər — island ekranı udmamalıdır.
          <span className="mt-1 text-xs text-gray-300/90 break-words rz-clamp-3">
            {description}
          </span>
        )}
        {actions && actions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => a.onClick(close)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                  ACTION_STYLE[a.variant ?? 'ghost']
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {large && (
        <button
          type="button"
          onClick={close}
          aria-label={tStatic('common.close')}
          className="shrink-0 -mt-1 -mr-2 p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

/**
 * Giriş/çıxış animasiyası.
 *
 * NİYƏ framer-motion DEYİL: toast-un DOM-dan silinməsini react-toastify idarə
 * edir, ona görə <AnimatePresence> burada işləmir (Kitchen-dəki köhnə island
 * öz state-ini özü saxladığı üçün orada işləyirdi). CSS keyframe toastify-ın
 * öz həyat dövrü ilə uyğunlaşır.
 *
 * collapseDuration çıxış animasiyasından SONRA başlayır: əvvəl island sönür,
 * sonra tutduğu hündürlük yığılır və altındakılar yumşaq şəkildə yuxarı sürüşür.
 */
export const islandTransition = cssTransition({
  enter: 'rz-island-in',
  exit: 'rz-island-out',
  collapseDuration: 300,
});

/**
 * Ekranda eyni anda görünə bilən bildiriş sayı.
 *
 * NİYƏ toastify-ın öz `limit`-i DEYİL: `limit` artıq olanı NÖVBƏYƏ salır —
 * yəni 4-cü bildiriş biri bağlanana qədər ümumiyyətlə görünmür. POS-da bu
 * pisdir: ən yeni hadisə (yeni sifariş, xəta) ən vacibidir və gözləməməlidir.
 * Ona görə növbə yox, SIXIŞDIRMA: yenisi gələndə ən köhnəsi bağlanır.
 */
const MAX_VISIBLE = 3;

/** Ekranda olan island-lar — köhnədən yeniyə doğru. */
const visible: Id[] = [];

function forget(id: Id): void {
  const i = visible.indexOf(id);
  if (i >= 0) visible.splice(i, 1);
}

function show(kind: NotifyKind, message: React.ReactNode, opts: NotifyOptions = {}): Id {
  // `position` və `icon` qəsdən ayrılır ki, toastify-a keçməsin — birincisi
  // toast-ı yenidən kənara atardı, ikincisi öz ikonumuzun yanında dublikat
  // ikon çəkərdi.
  const { title, description, actions, position: _pos, icon: _icon, onClose, ...rest } = opts;
  const text = typeof message === 'string' ? message : String(message ?? '');
  const large = isLarge(text, opts);

  // Yenisinə yer aç: ən köhnəsi (yuxarıdakı) bağlanır. dismiss() animasiyalıdır,
  // ona görə qısa müddət 4-ü birlikdə görünür — gedən sönərkən gələn sürüşür.
  while (visible.length >= MAX_VISIBLE) {
    const oldest = visible.shift();
    if (oldest !== undefined) rtToast.dismiss(oldest);
  }

  const id: Id = rtToast(
    <Island
      kind={kind}
      title={title ?? tStatic(KIND[kind].labelKey)}
      message={text}
      description={description}
      actions={actions}
      large={large}
    />,
    {
      ...rest,
      // 5 saniyə oxumaq üçün kifayətdir; iri bildirişdə mətn çox olduğuna görə
      // bir az uzun. Düymələri olan island isə cavab gözləyir — özü bağlanmır.
      autoClose: rest.autoClose ?? (actions?.length ? false : large ? 7000 : 5000),
      // Düymələr varsa təsadüfi klik onu bağlamamalıdır.
      closeOnClick: rest.closeOnClick ?? (!large && !actions?.length),
      onClose: (...args: unknown[]) => {
        forget(id);
        (onClose as ((...a: unknown[]) => void) | undefined)?.(...args);
      },
    },
  );

  // toastId ilə dublikat gələndə toastify mövcud id-ni qaytarır — iki dəfə
  // yazmayaq, yoxsa sıxışdırma sayı səhv olar.
  if (!visible.includes(id)) visible.push(id);
  return id;
}

export const notify = {
  success: (m: React.ReactNode, o?: NotifyOptions) => show('success', m, o),
  error: (m: React.ReactNode, o?: NotifyOptions) => show('error', m, o),
  warning: (m: React.ReactNode, o?: NotifyOptions) => show('warning', m, o),
  /** react-toastify uyğunluğu üçün alias. */
  warn: (m: React.ReactNode, o?: NotifyOptions) => show('warning', m, o),
  info: (m: React.ReactNode, o?: NotifyOptions) => show('info', m, o),
  /** Diqqət tələb edən hadisə: yeni sifariş, ofisant çağırışı. */
  alert: (m: React.ReactNode, o?: NotifyOptions) => show('alert', m, o),
  dismiss: rtToast.dismiss,
  isActive: rtToast.isActive,
  update: rtToast.update,
};

/**
 * Köhnə `import { toast } from 'react-toastify'` çağırışları üçün drop-in əvəz.
 * İmza eynidir, ona görə mövcud kod dəyişmədən yeni dizaynı alır.
 */
export const toast = notify;

export default notify;
